import {
  isCommandStep,
  isDefaultCommandStep,
  isMatrixCommandStep,
  isParallelCommandStep,
  isGroupStep,
  isIgnored,
  isWaitingOnConcurrencyGroup,
  isWaitingOnPlatformLimit,
  Step,
} from "~/lib/pipeline";
import { twMerge } from "tailwind-merge";
import { StepLink } from "~/components/build/Show/routes/steps/list/-components/TreeNode/StepLink";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import Emojify from "~/components/shared/Emojify";
import { useMostRecentJob, useBuild } from "~/components/build/Show/lib/BuildContext";
import CommandStepLabel from "~/components/build/Show/components/CommandStepLabel";
import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import { GroupStepCount } from "~/components/build/Show/components/GroupStepCount";
import { StatsJobCount } from "~/components/build/Show/components/StatsJobCount";
import { ReactNode, useMemo } from "react";
import { StepRetries } from "~/components/build/Show/routes/steps/list/-components/TreeNode/JobRetries";
import { QueueLabel } from "~/components/build/Show/routes/steps/list/-components/TreeNode/QueueLabel";
import { totalAnnotationCount } from "~/components/build/Show/components/AnnotationsBadge";
import { Badge } from "~/components/build/Show/routes/steps/list/-components/Badge";
import Icon from "~/components/shared/Icon";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { getStepTimingCell } from "~/components/build/Show/routes/steps/list/-components/timingColumns";
import { stepQueryOptions } from "~/components/build/Show/lib/queries/step";
import { stepJobsQueryOptions } from "~/components/build/Show/lib/queries/stepJobs";
import { usePrefetchHandlers } from "~/components/build/Show/lib/usePrefetchHandlers";
import { StepRow } from "~/components/build/Show/routes/steps/list/-components/StepRow";

interface Props {
  step: Step;
  arrow?: ReactNode;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLAnchorElement>) => void;
  className?: string;
  active?: boolean;
  failed?: boolean;
  depth?: number;
  isLoaded?: boolean;
}

const StepCount = ({ step }: { step: Step }) => {
  if (isMatrixCommandStep(step) || isParallelCommandStep(step)) {
    return <StatsJobCount step={step} />;
  }

  if (isGroupStep(step)) {
    return <GroupStepCount step={step} />;
  }

  return null;
};

/**
 * A catch-all step list item.
 */
export function StepItem({
  step,
  arrow,
  onClick,
  onKeyDown,
  className,
  active,
  failed,
  depth = 0,
  isLoaded,
}: Props) {
  const job = useMostRecentJob(step.uuid);
  const waitingOnConcurrencyGroup =
    isDefaultCommandStep(step) && isWaitingOnConcurrencyGroup(job?.state, step.statistics);
  const waitingOnPlatformLimit =
    isDefaultCommandStep(step) && isWaitingOnPlatformLimit(job?.state, step.statistics);
  const { build } = useBuild();
  const { missingDependencies } = useStepDependencies(step.uuid);

  const isStepIgnored = isIgnored(step);

  let label: ReactNode;
  let script: ReactNode | undefined;
  if (isCommandStep(step)) {
    label = <CommandStepLabel {...step} />;
    script = step.commandScript;
  } else {
    label = step.label ? <Emojify text={step.label} /> : null;
  }

  const timing = getStepTimingCell(step);

  const shouldOpenStepDrawer = useMemo(() => {
    if (
      isParallelCommandStep(step) ||
      isMatrixCommandStep(step) ||
      isGroupStep(step) ||
      isStepIgnored
    ) {
      return false;
    }
    return true;
  }, [step, isStepIgnored]);

  const { onMouseEnter, onMouseLeave } = usePrefetchHandlers({
    queries: [stepQueryOptions(build, step.uuid), stepJobsQueryOptions(build, step.uuid)],
    enabled: shouldOpenStepDrawer,
  });

  const annotationCountsByStyle = step.statistics?.annotationCountsByStyle;
  const annotationCount = annotationCountsByStyle
    ? totalAnnotationCount(annotationCountsByStyle)
    : 0;

  const retries = isDefaultCommandStep(step) ? <StepRetries step={step} /> : null;
  const queueKey = isDefaultCommandStep(step) ? step.statistics?.latestJobQueue : undefined;
  const queueOs = isDefaultCommandStep(step)
    ? step.statistics?.latestJobOperatingSystem
    : undefined;
  const latestJobId = isDefaultCommandStep(step) ? step.statistics?.latestJobId : undefined;
  const queueLabel = queueKey ? (
    <QueueLabel queueKey={queueKey} os={queueOs ?? undefined} latestJobId={latestJobId} />
  ) : null;

  return (
    <StepLink
      state={step.state}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={className}
      type={step.type}
      to={resolveStepUrl(step.uuid, { open: shouldOpenStepDrawer })}
      active={active}
      failed={failed}
      depth={depth}
      disabled={isStepIgnored}
      title={isStepIgnored ? "Skipped steps cannot be viewed" : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-loaded={isLoaded}
    >
      <StepRow
        depth={depth}
        chevron={arrow}
        stepType={step.type}
        icon={
          <StateIcon
            {...step}
            className={twMerge(
              textColorForStep({
                ...step,
                missingDependencies,
                waitingOnConcurrencyGroup,
                waitingOnPlatformLimit,
              }),
            )}
            missingDependencies={missingDependencies}
            waitingOnConcurrencyGroup={waitingOnConcurrencyGroup}
            waitingOnPlatformLimit={waitingOnPlatformLimit}
          />
        }
        label={label}
        script={script}
        stepStats={
          <>
            <StepCount step={step} />
            {retries}
          </>
        }
        meta={
          <span className="inline-flex items-center gap-2">
            {annotationCount > 0 && (
              <Badge className="inline-flex items-center gap-1">
                <Icon icon="sticky-note" style={{ fill: "none" }} className="h-3 w-3 shrink-0" />
                {annotationCount}
                <span className="container-hidden @xl/list:inline">
                  {annotationCount === 1 ? "annotation" : "annotations"}
                </span>
              </Badge>
            )}
            {queueLabel}
          </span>
        }
        timing={timing}
      />
    </StepLink>
  );
}
